import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { POSEIDON } from "./defaults";

const ORANGE = [242, 101, 34];   // #F26522
const BLACK  = [10, 10, 10];
const NAVY   = [25, 35, 60];
const GRAY   = [120, 120, 120];
const LIGHT  = [245, 245, 245];

// Loads /poseidon-logo.png as a base64 data URL so jsPDF can embed it
async function loadLogoDataURL() {
  try {
    const res = await fetch(POSEIDON.logo);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    /* PDF without logo is still useful — log silently */
    void e;
    return null;
  }
}

export async function generateProDosePDF(input, result) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 40;
  let y = 0;

  const logo = await loadLogoDataURL();

  // ===== HEADER BAND (black) =====
  doc.setFillColor(...BLACK);
  doc.rect(0, 0, W, 90, "F");
  doc.setFillColor(...ORANGE);
  doc.rect(0, 90, W, 6, "F");

  if (logo) {
    try { doc.addImage(logo, "PNG", M, 14, 60, 60); } catch (e) {}
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("POSEIDON", M + 75, 40);
  doc.setFontSize(10);
  doc.setTextColor(...ORANGE);
  doc.text("PRODOSE  ·  CHLORINE DOSING SPECIFICATION", M + 75, 58);
  doc.setTextColor(180, 180, 180);
  doc.setFontSize(8);
  doc.text(POSEIDON.website.toUpperCase(), M + 75, 72);

  // top-right date
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(200, 200, 200);
  doc.text(new Date().toLocaleString(), W - M, 40, { align: "right" });
  doc.setTextColor(...ORANGE);
  doc.setFont("helvetica", "bold");
  doc.text(POSEIDON.version, W - M, 56, { align: "right" });

  y = 120;

  // ===== PROJECT META =====
  doc.setFillColor(...LIGHT);
  doc.rect(M, y, W - 2 * M, 60, "F");
  const colW = (W - 2 * M) / 4;
  const meta = [
    ["PROJECT", input.project_name || "—"],
    ["TECHNICIAN", input.technician || "—"],
    ["SITE", input.site_address || "—"],
    ["STOCK", `${input.stock_chlorine_pct}% NaOCl`],
  ];
  meta.forEach((m, i) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...GRAY);
    doc.text(m[0], M + 10 + i * colW, y + 18);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...BLACK);
    doc.text(String(m[1]).substring(0, 30), M + 10 + i * colW, y + 40);
  });
  y += 80;

  // ===== HERO: TOTAL DOSE / PUMP / SETTING =====
  const heroW = (W - 2 * M - 16) / 3;
  // Tile 1 — orange
  doc.setFillColor(...ORANGE);
  doc.rect(M, y, heroW, 90, "F");
  doc.setTextColor(...BLACK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("TOTAL CHLORINE DOSE", M + 12, y + 18);
  doc.setFontSize(32);
  doc.text(String(result.dose_ppm), M + 12, y + 60);
  doc.setFontSize(9);
  doc.text("PPM", M + 12, y + 78);
  // Tile 2 — black
  const x2 = M + heroW + 8;
  doc.setFillColor(...BLACK);
  doc.rect(x2, y, heroW, 90, "F");
  doc.setTextColor(...ORANGE);
  doc.setFontSize(8);
  doc.text("RECOMMENDED PUMP", x2 + 12, y + 18);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.text(result.pump?.model || "—", x2 + 12, y + 56);
  doc.setFontSize(9);
  doc.setTextColor(200, 200, 200);
  doc.text(result.pump?.series || "", x2 + 12, y + 76);
  // Tile 3 — bordered light
  const x3 = x2 + heroW + 8;
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...BLACK);
  doc.setLineWidth(2);
  doc.rect(x3, y, heroW, 90, "FD");
  doc.setTextColor(...GRAY);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("PUMP SETTING", x3 + 12, y + 18);
  doc.setTextColor(...BLACK);
  doc.setFontSize(32);
  doc.text(`${result.pump_setting_pct}%`, x3 + 12, y + 60);
  doc.setFontSize(9);
  doc.text("STROKE", x3 + 12, y + 78);
  y += 110;

  // ===== KEY METRICS GRID =====
  autoTable(doc, {
    startY: y,
    head: [["BASE DEMAND", "PH MULT", "TEMP MULT", "CONTACT TIME", "TANK"]],
    body: [[
      `${result.base_demand_ppm} ppm`,
      `${result.ph_factor}×`,
      `${result.temp_factor}×`,
      `${result.contact_time_minutes} min`,
      result.retention_tank?.sku || "—",
    ]],
    theme: "grid",
    headStyles: { fillColor: BLACK, textColor: 255, fontStyle: "bold", fontSize: 8 },
    bodyStyles: { fontStyle: "bold", fontSize: 11, textColor: BLACK, halign: "center" },
    styles: { lineColor: [200, 200, 200], lineWidth: 0.5 },
    margin: { left: M, right: M },
  });
  y = doc.lastAutoTable.finalY + 16;

  // ===== STOCK + TANK + METER =====
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...BLACK);
  doc.text("CONSUMPTION · TANK · METER", M, y);
  y += 8;

  autoTable(doc, {
    startY: y,
    head: [["SPEC", "VALUE"]],
    body: [
      ["Stock chlorine (undiluted)", `${result.stock_gallons_per_day} gal/day`],
      ["Mix ratio", result.mix_ratio?.label || "—"],
      ["Diluted solution feed", `${result.diluted_gallons_per_day} gal/day`],
      ["Solution tank", `${result.solution_tank?.size_gallons} gal (refill ~${result.solution_tank?.estimated_days_supply} days)`],
      ["Retention tank", `${result.retention_tank?.name || "—"}${result.retention_tank?.gallons ? ` (${result.retention_tank.gallons} gal)` : ""}`],
      ["Water meter SKU", result.water_meter?.sku || "—"],
      ["Water meter spec", result.water_meter?.spec || "—"],
    ],
    theme: "striped",
    headStyles: { fillColor: ORANGE, textColor: 0, fontSize: 8 },
    bodyStyles: { fontSize: 9, textColor: BLACK },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    margin: { left: M, right: M },
  });
  y = doc.lastAutoTable.finalY + 16;

  // ===== PULSE MODE — two HERO tiles for the operator settings =====
  if (y > H - 180) { doc.addPage(); y = M; }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...BLACK);
  doc.text("PROPORTIONAL PULSE MODE  ·  STENNER PCM SETTINGS", M, y);
  y += 10;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.text("Stenner adjustable pumps have TWO operator controls — set BOTH on the pump dial / PCM.", M, y);
  y += 12;

  const pulseTileW = (W - 2 * M - 12) / 2;
  // Tile A — Seconds (orange fill)
  doc.setFillColor(...ORANGE);
  doc.rect(M, y, pulseTileW, 70, "F");
  doc.setTextColor(...BLACK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("(1) RUN TIME PER PULSE", M + 12, y + 18);
  doc.setFontSize(28);
  doc.text(`${result.pulse_mode.pump_on_seconds_per_pulse}`, M + 12, y + 50);
  doc.setFontSize(8);
  doc.text("SECONDS (PER 1 GAL WATER)", M + 12, y + 64);
  // Tile B — % (black fill, orange border)
  const tB = M + pulseTileW + 12;
  doc.setFillColor(...BLACK);
  doc.setDrawColor(...ORANGE);
  doc.setLineWidth(2);
  doc.rect(tB, y, pulseTileW, 70, "FD");
  doc.setTextColor(...ORANGE);
  doc.setFontSize(8);
  doc.text("(2) STROKE % SETTING", tB + 12, y + 18);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(28);
  doc.text(`${result.pulse_mode.pcm_setting_percent}%`, tB + 12, y + 50);
  doc.setTextColor(200, 200, 200);
  doc.setFontSize(8);
  doc.text("ON THE PUMP DIAL", tB + 12, y + 64);
  y += 84;

  autoTable(doc, {
    startY: y,
    head: [["PARAMETER", "VALUE"]],
    body: [
      ["Inline meter", `${result.pulse_mode.meter_pulses_per_gallon} pulse / gallon`],
      ["Solution per gallon water", `${result.pulse_mode.ml_of_solution_per_gallon_water} mL`],
    ],
    theme: "striped",
    headStyles: { fillColor: ORANGE, textColor: 0, fontSize: 8 },
    bodyStyles: { fontSize: 9, textColor: BLACK },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    margin: { left: M, right: M },
  });
  y = doc.lastAutoTable.finalY + 16;

  // ===== DEMAND BREAKDOWN =====
  if (y > H - 200) { doc.addPage(); y = M; }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("DEMAND BREAKDOWN (ppm Cl₂)", M, y);
  y += 8;
  autoTable(doc, {
    startY: y,
    head: [["SOURCE", "DEMAND (PPM)"]],
    body: Object.entries(result.breakdown).map(([k, v]) => [
      k.replace(/_/g, " ").replace(" ppm", "").toUpperCase(),
      String(v),
    ]),
    theme: "grid",
    headStyles: { fillColor: BLACK, textColor: 255, fontSize: 8 },
    bodyStyles: { fontSize: 9, textColor: BLACK },
    margin: { left: M, right: M },
  });
  y = doc.lastAutoTable.finalY + 16;

  // ===== SODA ASH =====
  if (result.soda_ash) {
    if (y > H - 200) { doc.addPage(); y = M; }
    const sa = result.soda_ash;
    doc.setFillColor(...ORANGE);
    doc.rect(M, y, W - 2 * M, 26, "F");
    doc.setTextColor(...BLACK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("SODA ASH CO-INJECTION  ·  pH ADJUSTMENT", M + 10, y + 17);
    y += 32;

    autoTable(doc, {
      startY: y,
      head: [["PARAMETER", "VALUE"]],
      body: [
        ["Current → target pH", `${sa.current_ph} → ${sa.target_ph} (Δ ${sa.delta_ph})`],
        ["Soda ash dose", `${sa.soda_ash_dose_ppm} ppm`],
        ["Stock consumption (undiluted)", `${sa.stock_gallons_per_day} gal/day`],
        ["Diluted feed", `${sa.diluted_gallons_per_day} gal/day`],
        ["Mix ratio", sa.mix_ratio?.label || "—"],
        ["Recommended pump", sa.pump ? `${sa.pump.model} @ ${sa.pump_setting_pct}%` : "—"],
        ["Soda ash powder", `${sa.lbs_powder_per_month} lb / month`],
        ["Solution tank", `${sa.solution_tank?.size_gallons} gal`],
        ["Recipe", sa.stock_recipe],
        ["Injection point", sa.injection_point],
      ],
      theme: "striped",
      headStyles: { fillColor: BLACK, textColor: 255, fontSize: 8 },
      bodyStyles: { fontSize: 9, textColor: BLACK },
      columnStyles: { 0: { cellWidth: 160, fontStyle: "bold" } },
      margin: { left: M, right: M },
    });
    y = doc.lastAutoTable.finalY + 16;
  }

  // ===== WARNINGS =====
  if (result.warnings && result.warnings.length) {
    if (y > H - 100) { doc.addPage(); y = M; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...ORANGE);
    doc.text("FIELD WARNINGS", M, y);
    y += 6;
    doc.setTextColor(...BLACK);
    result.warnings.forEach(w => {
      doc.setDrawColor(...ORANGE);
      doc.setLineWidth(3);
      doc.line(M, y + 4, M, y + 22);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const lines = doc.splitTextToSize(w, W - 2 * M - 14);
      doc.text(lines, M + 10, y + 14);
      y += Math.max(24, 14 + lines.length * 10);
    });
    y += 8;
  }

  // ===== SHOCK PROTOCOL — intentionally omitted in v0.4 (biofilm flag is handled by a flush-the-plumbing warning above) =====

  // ===== FOOTER on every page =====
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFillColor(...BLACK);
    doc.rect(0, H - 28, W, 28, "F");
    doc.setFillColor(...ORANGE);
    doc.rect(0, H - 32, W, 4, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(POSEIDON.brand.toUpperCase(), M, H - 12);
    doc.setTextColor(...ORANGE);
    doc.text(POSEIDON.website.toUpperCase(), W / 2, H - 12, { align: "center" });
    doc.setTextColor(200, 200, 200);
    doc.setFont("helvetica", "normal");
    doc.text(`PAGE ${i} / ${pageCount}`, W - M, H - 12, { align: "right" });
  }

  // ===== SAVE =====
  const safeName = (input.project_name || "ProDose").replace(/[^a-z0-9]+/gi, "_").slice(0, 40);
  doc.save(`Poseidon_ProDose_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`);
}
